package terraultima.web

import terraultima.Content
import terraultima.Fmt

/**
 * The engine's JavaScript build, verified without a browser.
 *
 * The simulation is the same source as the JVM build and is covered by 61 JVM tests, so what needs
 * proving here is narrower and entirely platform-specific: that `kotlinx-serialization` parses the
 * corpus under Kotlin/JS, that the hand-rolled [Fmt] agrees with the `String.format` it replaced,
 * that the replay collector actually reaches a decision, and that replaying the same seed and the
 * same taps twice gives the same century — because the whole persistence model of the web build is
 * that it does.
 *
 * It also measures what a tap costs, which is the one number that decides whether replay-per-tap is
 * a legitimate architecture or a clever mistake.
 *
 * Run with `./play web-check`. Nothing here is loaded by the browser build beyond a `typeof
 * document` test in `main`.
 */
object SelfCheck {

    private val fs: dynamic = js("require('fs')")

    private fun readCorpus(): Map<String, String> {
        val candidates = listOf(
            "src/main/resources/content",
            "../src/main/resources/content",
            "../../src/main/resources/content",
            "../../../src/main/resources/content",
            "../../../../src/main/resources/content",
            "../../../../../src/main/resources/content",
            "../../../../../../src/main/resources/content"
        )
        for (dir in candidates) {
            val ok = try { fs.existsSync("$dir/events.json") as Boolean } catch (e: Throwable) { false }
            if (ok) return Content.FILES.associateWith { fs.readFileSync("$dir/$it", "utf8") as String }
        }
        throw RuntimeException("could not find src/main/resources/content from " +
                (js("process.cwd()") as String))
    }

    private var failures = 0
    private fun check(ok: Boolean, what: String) {
        if (ok) println("  ok    $what") else { failures++; println("  FAIL  $what") }
    }

    fun run() {
        println("== TERRA ULTIMA — the engine, in JavaScript ==")
        println("node ${js("process.version") as String}\n")

        println("-- formatting (Fmt replaced String.format, which JS does not have) --")
        check(Fmt.dec(2.5, 1) == "2.5", "dec(2.5, 1) = ${Fmt.dec(2.5, 1)}")
        check(Fmt.dec(0.0, 2) == "0.00", "dec(0.0, 2) = ${Fmt.dec(0.0, 2)}")
        check(Fmt.dec(-1.25, 1) == "-1.3" || Fmt.dec(-1.25, 1) == "-1.2",
            "dec(-1.25, 1) = ${Fmt.dec(-1.25, 1)}")
        check(Fmt.dec(1999.96, 1) == "1,999.96".let { "2000.0" }, "dec(1999.96, 1) = ${Fmt.dec(1999.96, 1)}")
        check(Fmt.dec(0.999, 2) == "1.00", "dec(0.999, 2) = ${Fmt.dec(0.999, 2)}")
        check(Fmt.int(2.5) == "3" || Fmt.int(2.5) == "2", "int(2.5) = ${Fmt.int(2.5)}")
        check(Fmt.int(-0.4) == "0", "int(-0.4) = ${Fmt.int(-0.4)}")
        check(Fmt.sig2(0.0345).startsWith("0.03"), "sig2(0.0345) = ${Fmt.sig2(0.0345)}")

        println("\n-- the corpus --")
        Content.loadFrom(readCorpus())
        check(Content.corpus.events.size > 50, "${Content.corpus.events.size} events parsed")
        check(Content.corpus.catastrophes.isNotEmpty(),
            "${Content.corpus.catastrophes.size} premises")
        val errs = Content.validate()
        check(errs.isEmpty(), "content validates clean" +
                (if (errs.isEmpty()) "" else " — ${errs.size} problems: ${errs.first()}"))

        println("\n-- every premise reaches a decision --")
        for ((n, cata) in Content.corpus.catastrophes.withIndex()) {
            val setup = Run.replay(42, n, emptyList(), launched = false)
            val first = Run.replay(42, n, emptyList(), launched = true)
            check(setup.setup != null && first.decision != null,
                "${cata.id}: expedition + first decision (${first.decision?.title ?: "none"})")
        }

        println("\n-- a hundred years, one tap at a time --")
        var slowest = 0.0
        var slowestAt = 0
        for (seed in listOf(42, 7, 401)) {
            val choices = mutableListOf<Int>()
            var decisions = 0
            var ending: String? = null
            while (decisions < 400) {
                val t0 = js("Date.now()") as Double
                val r = Run.replay(seed, 0, choices.toList(), launched = true)
                val ms = (js("Date.now()") as Double) - t0
                if (ms > slowest) { slowest = ms; slowestAt = choices.size }
                if (r.ending != null) { ending = r.ending!!.tier; break }
                val d = r.decision ?: break
                choices += d.options.first { it.available }.number
                decisions++
            }
            check(ending != null, "seed $seed: $decisions decisions, ended $ending")
        }
        println("  slowest replay: ${Fmt.dec(slowest, 0)} ms (at ${slowestAt} decisions in)")

        println("\n-- the same seed and the same taps make the same century --")
        val a = Run.replay(1234, 2, listOf(1, 1, 1, 1, 1, 1, 1, 1), launched = true)
        val b = Run.replay(1234, 2, listOf(1, 1, 1, 1, 1, 1, 1, 1), launched = true)
        check(a.decision?.debugId == b.decision?.debugId && a.decision?.debugId != null,
            "replayed twice: ${a.decision?.debugId} == ${b.decision?.debugId}")
        check(a.snap?.colony?.souls == b.snap?.colony?.souls,
            "same population: ${a.snap?.colony?.souls}")
        val c = Run.replay(1234, 2, listOf(1, 1, 1, 1, 1, 1, 1, 2), launched = true)
        check(c.decision?.debugId != a.decision?.debugId || c.snap?.colony?.souls != a.snap?.colony?.souls,
            "one different tap makes a different century")

        println()
        if (failures == 0) println("ALL CHECKS PASSED")
        else {
            println("$failures CHECK(S) FAILED")
            js("process.exitCode = 1")
        }
    }
}
